/* @license
 * Copyright 2025 Next Reality. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { BufferAttribute, CanvasTexture, ClampToEdgeWrapping, Color, DoubleSide, FrontSide, Group, Mesh, MeshBasicMaterial, MeshPhysicalMaterial, MeshStandardMaterial, SRGBColorSpace, Vector3 } from 'three';
/**
 * Convert any image source to canvas
 */
function toCanvas(source) {
    const canvas = document.createElement('canvas');
    const w = ('width' in source && source.width) || ('videoWidth' in source && source.videoWidth) || 0;
    const h = ('height' in source && source.height) || ('videoHeight' in source && source.videoHeight) || 0;
    if (!w || !h)
        return null;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return null;
    ctx.drawImage(source, 0, 0, w, h);
    return canvas;
}
/**
 * Get next power-of-two value
 */
function nextPOT(n) {
    let p = 1;
    while (p < n)
        p <<= 1;
    return p;
}
/**
 * Resize canvas to power-of-two dimensions
 */
function resizeCanvasPOT(canvas, maxSize = 1024) {
    const potW = Math.min(nextPOT(canvas.width), maxSize);
    const potH = Math.min(nextPOT(canvas.height), maxSize);
    if (potW === canvas.width && potH === canvas.height)
        return canvas;
    const out = document.createElement('canvas');
    out.width = potW;
    out.height = potH;
    const ctx = out.getContext('2d');
    if (ctx) {
        ctx.drawImage(canvas, 0, 0, potW, potH);
    }
    return out;
}
/**
 * Build a fresh CanvasTexture for USDZ (iOS-safe)
 * - Creates new canvas from the source image
 * - Resizes to power-of-two, capped at maxSize
 * - Copies UV transforms & sampler settings
 */
export function makeIOSCanvasTexture(srcTex, options = {}) {
    var _a, _b;
    const { forcePOT = true, maxSize = 1024 } = options;
    if (!srcTex || !srcTex.image)
        return null;
    // Materialize pixels into a same-origin canvas
    let canvas = toCanvas(srcTex.image);
    if (!canvas)
        return null;
    // iOS safety: use POT and cap size
    if (forcePOT) {
        canvas = resizeCanvasPOT(canvas, maxSize);
    }
    // Build a fresh CanvasTexture
    const tex = new CanvasTexture(canvas);
    tex.flipY = false;
    tex.colorSpace = SRGBColorSpace;
    // Copy sampler/UV transform
    tex.wrapS = srcTex.wrapS;
    tex.wrapT = srcTex.wrapT;
    tex.anisotropy = (_a = srcTex.anisotropy) !== null && _a !== void 0 ? _a : 1;
    if (srcTex.offset)
        tex.offset.copy(srcTex.offset);
    if (srcTex.repeat)
        tex.repeat.copy(srcTex.repeat);
    if (srcTex.center && tex.center)
        tex.center.copy(srcTex.center);
    tex.rotation = (_b = srcTex.rotation) !== null && _b !== void 0 ? _b : 0;
    // iOS is happiest with Clamp for simple planes
    tex.wrapS = ClampToEdgeWrapping;
    tex.wrapT = ClampToEdgeWrapping;
    tex.needsUpdate = true;
    return tex;
}
/**
 * Flip triangle winding order for geometries with negative determinant
 */
export function flipTriangleWinding(geometry) {
    // Ensure the geometry is indexed so we can swap indices per triangle
    if (!geometry.getIndex()) {
        const count = geometry.attributes.position.count;
        const index = new Uint32Array(count);
        for (let i = 0; i < count; i++)
            index[i] = i;
        geometry.setIndex(new BufferAttribute(index, 1));
    }
    const index = geometry.getIndex();
    const arr = index.array;
    for (let i = 0; i < arr.length; i += 3) {
        // Swap first two indices to flip the winding
        const tmp = arr[i + 0];
        arr[i + 0] = arr[i + 1];
        arr[i + 1] = tmp;
    }
    index.needsUpdate = true;
    // If normals exist, invert them to match flipped winding
    const n = geometry.attributes.normal;
    if (n) {
        for (let i = 0; i < n.count; i++) {
            n.setX(i, -n.getX(i));
            n.setY(i, -n.getY(i));
            n.setZ(i, -n.getZ(i));
        }
        n.needsUpdate = true;
    }
}
/**
 * Check if geometry is very thin (likely a plane)
 */
export function isVeryThin(geometry, eps = 1e-6) {
    geometry.computeBoundingBox();
    const s = new Vector3();
    geometry.boundingBox.getSize(s);
    return s.x < eps || s.y < eps || s.z < eps;
}
/**
 * Check if a material is USDZ-compatible
 * USDZ supports: MeshStandardMaterial, MeshPhysicalMaterial, MeshBasicMaterial (with limitations)
 */
function isUSDZCompatibleMaterial(material) {
    // Check if it's a standard PBR material
    if (material instanceof MeshStandardMaterial || material instanceof MeshPhysicalMaterial) {
        return true;
    }
    // MeshBasicMaterial can work but is less ideal
    if (material instanceof MeshBasicMaterial) {
        return true;
    }
    return false;
}
/**
 * Check if an object needs enhanced sanitization and what types
 * Returns true if the object has:
 * - Non-standard materials (incompatible with USDZ)
 * - Negative determinant transforms (flipped/mirrored)
 * - DoubleSide materials (USDZ doesn't support DoubleSide)
 * - VideoTextures (need conversion to canvas)
 */
export function needsEnhancedSanitization(object) {
    return analyzeSanitizationNeeds(object).needsSanitization;
}
/**
 * Analyzes what sanitization is needed for an object
 */
export function analyzeSanitizationNeeds(object) {
    let hasIncompatibleMaterial = false;
    let hasNegativeDeterminant = false;
    let hasDoubleSideMaterial = false;
    let hasVideoTexture = false;
    object.updateWorldMatrix(true, true);
    object.traverse((node) => {
        if (!node.isMesh)
            return;
        const mesh = node;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        // Check for negative determinant
        const det = mesh.matrixWorld.determinant();
        if (det < 0) {
            hasNegativeDeterminant = true;
            console.log(`Detected negative determinant on mesh: ${mesh.name}`);
        }
        // Check materials
        for (const material of materials) {
            // Check if material is USDZ-compatible
            if (!isUSDZCompatibleMaterial(material)) {
                hasIncompatibleMaterial = true;
                console.log(`Detected incompatible material: ${material.type} on mesh: ${mesh.name}`);
            }
            // Check for DoubleSide
            if (material.side === DoubleSide) {
                hasDoubleSideMaterial = true;
                console.log(`Detected DoubleSide material on mesh: ${mesh.name}`);
            }
            // Check for VideoTexture
            const mat = material;
            if (mat.map && mat.map.isVideoTexture) {
                hasVideoTexture = true;
                console.log(`Detected VideoTexture on mesh: ${mesh.name}`);
            }
        }
    });
    const needsSanitization = hasIncompatibleMaterial || hasNegativeDeterminant || hasDoubleSideMaterial || hasVideoTexture;
    if (needsSanitization) {
        console.log('Enhanced sanitization required:', {
            incompatibleMaterial: hasIncompatibleMaterial,
            negativeDeterminant: hasNegativeDeterminant,
            doubleSide: hasDoubleSideMaterial,
            videoTexture: hasVideoTexture
        });
    }
    else {
        console.log('Model is USDZ-compatible, skipping enhanced sanitization');
    }
    return {
        hasIncompatibleMaterial,
        hasNegativeDeterminant,
        hasDoubleSideMaterial,
        hasVideoTexture,
        needsSanitization
    };
}
/**
 * Prepare an Object3D for USDZ export in a Quick Look-safe way:
 * - Bake world transforms into geometry (no TRS left)
 * - Fix negative-determinant nodes by flipping winding
 * - Optionally duplicate thin meshes to get explicit back faces
 * - Optionally convert textures to iOS-safe canvas format
 *
 * This enhanced sanitization handles edge cases that the basic
 * sanitizeForUSDZ might miss, particularly:
 * - Negative scale determinants (flipped models)
 * - Thin geometry requiring two-sided rendering
 * - Texture compatibility issues on iOS
 */
export function sanitizeForUSDZEnhanced(object, options = {}) {
    const { twoSidedThinMeshes = true, convertTexturesToCanvas = false, thinGeometryThreshold = 1e-6, forcePOT = true, maxCanvasSize = 1024 } = options;
    // Update world matrix first
    object.updateWorldMatrix(true, true);
    // We'll flatten transforms into geometry and rebuild under a fresh Group
    const out = new Group();
    out.name = (object.name || 'USDZRoot') + '_Sanitized';
    // Process original object directly (preserve textures)
    object.traverse((node) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
        // Skip lights, cameras, and helpers (shouldn't be exported)
        if (node.isLight || node.isCamera || node.isHelper) {
            console.log(`Skipping non-exportable node: ${node.type}`);
            return;
        }
        if (!node.isMesh || !node.geometry)
            return;
        const mesh = node;
        // Clone and bake world matrix into the geometry
        const geom = mesh.geometry.clone();
        const mw = mesh.matrixWorld.clone();
        const det = mw.determinant();
        console.log(`Processing mesh: ${mesh.name}, determinant: ${det.toFixed(4)}`);
        geom.applyMatrix4(mw);
        // Get material and texture from original node
        const srcMat = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
        const srcMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.map) || null;
        let pbrMapFront = null;
        let pbrMapBack = null;
        // Handle texture conversion - only convert when necessary
        if (srcMap) {
            // Always handle VideoTexture (must convert to canvas for USDZ export)
            if (srcMap.isVideoTexture) {
                const videoTexture = srcMap;
                const video = videoTexture.image;
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth || 512;
                canvas.height = video.videoHeight || 512;
                const ctx = canvas.getContext('2d');
                if (ctx && video.readyState >= 2) {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const canvasTexture = new CanvasTexture(canvas);
                    canvasTexture.minFilter = srcMap.minFilter;
                    canvasTexture.magFilter = srcMap.magFilter;
                    canvasTexture.colorSpace = srcMap.colorSpace;
                    canvasTexture.wrapS = ClampToEdgeWrapping;
                    canvasTexture.wrapT = ClampToEdgeWrapping;
                    pbrMapFront = canvasTexture;
                    pbrMapBack = canvasTexture.clone();
                    console.log(`  - Converted VideoTexture to canvas for USDZ compatibility`);
                }
            }
            else if (convertTexturesToCanvas) {
                // Only convert if explicitly requested (for problematic image textures)
                pbrMapFront = makeIOSCanvasTexture(srcMap, { forcePOT, maxSize: maxCanvasSize });
                pbrMapBack = makeIOSCanvasTexture(srcMap, { forcePOT, maxSize: maxCanvasSize });
                if (pbrMapFront) {
                    console.log(`  - Created iOS-safe texture: ${(_a = pbrMapFront.image) === null || _a === void 0 ? void 0 : _a.width}x${(_b = pbrMapFront.image) === null || _b === void 0 ? void 0 : _b.height} (POT, sRGB)`);
                }
            }
            else {
                // Default: Keep original textures (most GLB models work fine as-is)
                pbrMapFront = srcMap;
                pbrMapBack = srcMap;
                console.log(`  - Using original texture without conversion`);
            }
        }
        // Build material - preserve original properties unless we converted textures
        const isVideoTextureConverted = srcMap && srcMap.isVideoTexture;
        const materialConfig = {
            map: pbrMapFront || null,
            side: FrontSide,
            depthWrite: true
        };
        // Only apply iOS-specific workarounds when we actually converted textures
        if (convertTexturesToCanvas || isVideoTextureConverted) {
            // For converted textures, use iOS-safe properties
            materialConfig.color = new Color(0xffffff);
            materialConfig.metalness = (_c = srcMat === null || srcMat === void 0 ? void 0 : srcMat.metalness) !== null && _c !== void 0 ? _c : 0;
            materialConfig.roughness = (_d = srcMat === null || srcMat === void 0 ? void 0 : srcMat.roughness) !== null && _d !== void 0 ? _d : 1;
            materialConfig.transparent = false;
            materialConfig.opacity = 1;
            materialConfig.alphaTest = 0.001;
            materialConfig.toneMapped = false;
        }
        else {
            // For standard materials, preserve all original properties
            materialConfig.color = ((_e = srcMat === null || srcMat === void 0 ? void 0 : srcMat.color) === null || _e === void 0 ? void 0 : _e.clone()) || new Color(0xffffff);
            materialConfig.metalness = (_f = srcMat === null || srcMat === void 0 ? void 0 : srcMat.metalness) !== null && _f !== void 0 ? _f : 0;
            materialConfig.roughness = (_g = srcMat === null || srcMat === void 0 ? void 0 : srcMat.roughness) !== null && _g !== void 0 ? _g : 1;
            materialConfig.transparent = !!(srcMat === null || srcMat === void 0 ? void 0 : srcMat.transparent);
            materialConfig.opacity = (_h = srcMat === null || srcMat === void 0 ? void 0 : srcMat.opacity) !== null && _h !== void 0 ? _h : 1;
            materialConfig.alphaTest = (_j = srcMat === null || srcMat === void 0 ? void 0 : srcMat.alphaTest) !== null && _j !== void 0 ? _j : 0;
            materialConfig.emissive = ((_k = srcMat === null || srcMat === void 0 ? void 0 : srcMat.emissive) === null || _k === void 0 ? void 0 : _k.clone()) || new Color(0x000000);
            materialConfig.emissiveIntensity = (_l = srcMat === null || srcMat === void 0 ? void 0 : srcMat.emissiveIntensity) !== null && _l !== void 0 ? _l : 0;
            materialConfig.emissiveMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.emissiveMap) || null;
            materialConfig.aoMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.aoMap) || null;
            materialConfig.aoMapIntensity = (_m = srcMat === null || srcMat === void 0 ? void 0 : srcMat.aoMapIntensity) !== null && _m !== void 0 ? _m : 1;
            materialConfig.normalMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.normalMap) || null;
            materialConfig.normalScale = (_o = srcMat === null || srcMat === void 0 ? void 0 : srcMat.normalScale) === null || _o === void 0 ? void 0 : _o.clone();
            materialConfig.metalnessMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.metalnessMap) || null;
            materialConfig.roughnessMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.roughnessMap) || null;
        }
        const frontMat = new MeshStandardMaterial(materialConfig);
        // If world determinant is negative, fix winding so front faces are correct
        if (det < 0) {
            console.warn(`⚠️ Negative determinant on ${mesh.name} - fixing winding order`);
            flipTriangleWinding(geom);
        }
        // Recompute normals/bounds after baking
        if (!geom.attributes.normal)
            geom.computeVertexNormals();
        geom.computeBoundingBox();
        geom.computeBoundingSphere();
        // Add the front-facing mesh
        const frontMesh = new Mesh(geom, frontMat);
        frontMesh.name = (mesh.name || 'Mesh') + '_Front';
        out.add(frontMesh);
        // For very thin geometry (planes), add a back-facing duplicate so both sides show
        if (twoSidedThinMeshes && isVeryThin(geom, thinGeometryThreshold)) {
            console.log(`Creating explicit back face for thin mesh: ${mesh.name}`);
            const backGeom = geom.clone();
            flipTriangleWinding(backGeom); // flip again to make a back face
            // Create back material config (same as front)
            const backMaterialConfig = {
                map: pbrMapBack || null,
                side: FrontSide,
                depthWrite: true
            };
            // Apply iOS-specific workarounds only for converted textures
            if (convertTexturesToCanvas || isVideoTextureConverted) {
                backMaterialConfig.color = new Color(0xffffff);
                backMaterialConfig.metalness = (_p = srcMat === null || srcMat === void 0 ? void 0 : srcMat.metalness) !== null && _p !== void 0 ? _p : 0;
                backMaterialConfig.roughness = (_q = srcMat === null || srcMat === void 0 ? void 0 : srcMat.roughness) !== null && _q !== void 0 ? _q : 1;
                backMaterialConfig.transparent = false;
                backMaterialConfig.opacity = 1;
                backMaterialConfig.alphaTest = 0.001;
                backMaterialConfig.toneMapped = false;
            }
            else {
                // Preserve all original material properties
                backMaterialConfig.color = ((_r = srcMat === null || srcMat === void 0 ? void 0 : srcMat.color) === null || _r === void 0 ? void 0 : _r.clone()) || new Color(0xffffff);
                backMaterialConfig.metalness = (_s = srcMat === null || srcMat === void 0 ? void 0 : srcMat.metalness) !== null && _s !== void 0 ? _s : 0;
                backMaterialConfig.roughness = (_t = srcMat === null || srcMat === void 0 ? void 0 : srcMat.roughness) !== null && _t !== void 0 ? _t : 1;
                backMaterialConfig.transparent = !!(srcMat === null || srcMat === void 0 ? void 0 : srcMat.transparent);
                backMaterialConfig.opacity = (_u = srcMat === null || srcMat === void 0 ? void 0 : srcMat.opacity) !== null && _u !== void 0 ? _u : 1;
                backMaterialConfig.alphaTest = (_v = srcMat === null || srcMat === void 0 ? void 0 : srcMat.alphaTest) !== null && _v !== void 0 ? _v : 0;
                backMaterialConfig.emissive = ((_w = srcMat === null || srcMat === void 0 ? void 0 : srcMat.emissive) === null || _w === void 0 ? void 0 : _w.clone()) || new Color(0x000000);
                backMaterialConfig.emissiveIntensity = (_x = srcMat === null || srcMat === void 0 ? void 0 : srcMat.emissiveIntensity) !== null && _x !== void 0 ? _x : 0;
                backMaterialConfig.emissiveMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.emissiveMap) || null;
                backMaterialConfig.aoMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.aoMap) || null;
                backMaterialConfig.aoMapIntensity = (_y = srcMat === null || srcMat === void 0 ? void 0 : srcMat.aoMapIntensity) !== null && _y !== void 0 ? _y : 1;
                backMaterialConfig.normalMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.normalMap) || null;
                backMaterialConfig.normalScale = (_z = srcMat === null || srcMat === void 0 ? void 0 : srcMat.normalScale) === null || _z === void 0 ? void 0 : _z.clone();
                backMaterialConfig.metalnessMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.metalnessMap) || null;
                backMaterialConfig.roughnessMap = (srcMat === null || srcMat === void 0 ? void 0 : srcMat.roughnessMap) || null;
            }
            const backMat = new MeshStandardMaterial(backMaterialConfig);
            const backMesh = new Mesh(backGeom, backMat);
            backMesh.name = (mesh.name || 'Mesh') + '_Back';
            out.add(backMesh);
        }
    });
    return out;
}
//# sourceMappingURL=usdz-utils.js.map